<template>
    <div class="container homepage-banners-container">
        <div v-if="validBanners.length >= 2 && validCarouselBanners.length >= 1" class="homepage-banners">
            <div class="homepage-banners__carousel">
                <Carousel
                    v-if="validCarouselBanners.length >= 1"
                    v-model="slide"
                    :transition="600"
                    :autoplay="5000"
                    :wrap-around="validCarouselBanners.length > 1"
                    :pause-autoplay-on-hover="true"
                >
                    <Slide v-for="banner in validCarouselBanners" :key="banner.id">
                        <div
                            ref="bannerElements"
                            class="homepage-banners__item homepage-banners__item--large"
                            :class="resolveColorVariantCssVariant(banner)"
                            :data-id="banner.id"
                            :data-title="banner.title"
                        >
                            <div
                                class="homepage-banners__background homepage-banners__background--desktop"
                                :style="{ backgroundImage: `url(${banner.image})` }"
                            ></div>
                            <div
                                class="homepage-banners__background homepage-banners__background--mobile"
                                :style="{ backgroundImage: `url(${banner.mobileImage || banner.image})` }"
                            ></div>
                            <a :href="banner.url" tabindex="-1">
                                <h2>{{ banner.title }}</h2>
                                <p v-html="banner.description"></p>
                                <span>→</span>
                            </a>
                        </div>
                    </Slide>
                </Carousel>
            </div>

            <template v-for="i in 2" :key="i">
                <div v-if="validBanners[i - 1]" class="homepage-banners__item" :class="resolveColorVariantCssVariant(validBanners[i - 1])">
                    <div
                        class="homepage-banners__background homepage-banners__background--desktop"
                        :style="{ backgroundImage: `url(${validBanners[i - 1].image})` }"
                    ></div>
                    <div
                        class="homepage-banners__background homepage-banners__background--mobile"
                        :style="{ backgroundImage: `url(${validBanners[i - 1].mobileImage || validBanners[i - 1].image})` }"
                    ></div>
                    <a :href="validBanners[i - 1].url">
                        <h2>{{ validBanners[i - 1].title }}</h2>
                        <p v-html="validBanners[i - 1].description"></p>
                        <span>→</span>
                    </a>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { MetaObject } from '@nuxt/schema'
import { Carousel, Slide } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'
import { useMatomoClickTrack, useMatomoViewportTrack } from '~/composables/useMatomo'
import BannerTopCategory from '~/models/BannerTopCategory'

const props = defineProps<{
    banners: BannerTopCategory[]
    carouselBanners: BannerTopCategory[]
    matomoBannerCategory: string
}>()

const bannerElements = ref([])
const slide = ref(0)

const head: MetaObject = {
    link: [],
}
props.carouselBanners.forEach((banner: BannerTopCategory, index: number) => {
    if (index < 1) {
        head.link?.push({
            rel: 'preload',
            href: banner.image,
            as: 'image',
        })
    }
})
props.banners.forEach((banner: BannerTopCategory, index: number) => {
    if (index < 2) {
        head.link?.push({
            rel: 'preload',
            href: banner.image,
            as: 'image',
        })
    }
})
useHead(head)

const validBanners = computed(() => {
    const items: BannerTopCategory[] = []
    props.banners.forEach((banner: BannerTopCategory) => {
        try {
            new URL(banner.url)

            items.push(banner)
        } catch {}
    })

    return items
})

const validCarouselBanners = computed(() => {
    const items: BannerTopCategory[] = []
    props.carouselBanners.forEach((banner: BannerTopCategory) => {
        try {
            if (banner.url === '') {
                items.push(banner)

                return
            }

            new URL(banner.url)

            items.push(banner)
        } catch {}
    })

    return items
})

onMounted(() => {
    if (bannerElements.value) {
        bannerElements.value.forEach((item: HTMLElement) => {
            useMatomoViewportTrack(item, {
                category: props.matomoBannerCategory,
                action: 'view',
                name: item.dataset.title,
                value: +(item.dataset.id ?? 0),
            })

            useMatomoClickTrack(item, {
                category: props.matomoBannerCategory,
                action: 'click',
                name: item.dataset.title,
                value: +(item.dataset.id ?? 0),
            })
        })
    }
})

const resolveColorVariantCssVariant = (banner: BannerTopCategory) => {
    if (banner.colorVariant === 'white') {
        return 'homepage-banners__item--white'
    }
    if (banner.colorVariant === 'dark') {
        return 'homepage-banners__item--dark'
    }

    return 'homepage-banners__item--brand'
}
</script>

<style lang="scss" scoped>
.homepage-banners-container {
    padding: 0 0;

    @include screen-md {
        padding: 0 15px;
    }
}
.homepage-banners {
    display: grid;
    gap: 7px;
    grid-template-columns: 100%;
    margin: 10px auto 0;

    @include screen-md {
        grid-template-columns: calc((100% - 10px) * 5 / 7) auto;
        gap: 10px;
        margin: 25px 0 0;
    }

    &__carousel {
        position: relative;
        grid-row-end: 3;
        grid-row-start: 1;
        overflow: hidden;
        width: 100%;
    }

    @keyframes zoom-in {
        from {
            background-size: 100%;
        }
        to {
            background-size: 105%;
        }
    }

    @keyframes zoom-out {
        from {
            background-size: 105%;
        }
        to {
            background-size: 100%;
        }
    }

    &__background {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: 10px;

        &:hover {
            animation: zoom-in 0.5s forwards;
        }

        &:not(:hover) {
            animation: zoom-out 0.5s forwards;
        }

        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0);
            transition: background 0.5s ease-in-out;
            z-index: 1;
            border-radius: 10px;
        }

        &:hover::after {
            background: rgba(0, 0, 0, 0.1);
        }

        &--desktop {
            display: none;

            @include screen-md {
                display: block;
            }
        }

        &--mobile {
            display: block;

            @include screen-md {
                display: none;
            }
        }
    }

    &__item {
        position: relative;
        min-width: 100%;
        aspect-ratio: 2 / 1;
        transition: background-size 0.5s ease-in-out;
        text-align: left;
        container-type: inline-size;

        @include screen-md {
            aspect-ratio: auto;
            border-radius: 10px;
            text-align: left;
        }

        a {
            padding: 3px 10px;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            flex-direction: column;
            width: 100%;
            height: 100%;
            cursor: pointer;
            z-index: 2;

            @container (min-width: 300px) {
                padding: 0 15px;
            }

            @container (min-width: 600px) {
                padding: 0 30px;
            }
        }

        &--large {
            aspect-ratio: 8 / 5;

            @include screen-md {
                aspect-ratio: 5 / 2;
            }

            a {
                justify-content: center;

                @container (min-width: 300px) {
                    padding: 0 15px 0;
                }

                @container (min-width: 600px) {
                    padding: 30px 30px;
                }

                @include screen-md {
                    @container (min-width: 600px) {
                        padding: 30px 30px;
                        justify-content: flex-end;
                    }
                }
            }
        }

        h2 {
            margin: 0;
            padding: 0;
            font-size: 15px;
            line-height: 21px;
            font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
                'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-variation-settings: 'wght' 700, 'wdth' 125;

            @container (min-width: 230px) {
                font-size: 23px;
                line-height: 27px;
            }

            @container (min-width: 300px) {
                font-size: 27px;
                line-height: 40px;
            }

            @container (min-width: 358px) {
                font-size: 30px;
                line-height: 40px;
            }

            @container (min-width: 380px) {
                font-size: 35px;
                line-height: 40px;
            }

            @container (min-width: 500px) {
                font-size: 37px;
                line-height: 47px;
            }

            @container (min-width: 600px) {
                font-size: 47px;
                line-height: 57px;
            }

            @container (min-width: 712px) {
                font-size: 57px;
                line-height: 67px;
            }

            @container (min-width: 816px) {
                font-size: 67px;
                line-height: 77px;
            }

            @container (min-width: 914px) {
                font-size: 77px;
                line-height: 87px;
            }

            @container (min-width: 990px) {
                font-size: 84px;
                line-height: 94px;
            }
        }

        p {
            padding: 0;
            font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
                'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-variation-settings: 'wght' 300, 'wdth' 100;
            max-width: 50%;

            @container (min-width: 1px) {
                margin: 0px 0 2px;
                font-size: 12px;
                line-height: 16px;
            }

            @container (min-width: 230px) {
                margin: 0px 0 2px;
                font-size: 14px;
                line-height: 20px;
            }

            @container (min-width: 300px) {
                margin: 0px 0 4px;
                font-size: 16px;
                line-height: 21px;
            }

            @container (min-width: 330px) {
                margin: 0 0 4px;
                font-size: 20px;
                line-height: 27px;
            }

            @container (min-width: 380px) {
                margin: 2px 0 6px;
                font-size: 23px;
                line-height: 34px;
            }

            @container (min-width: 391px) {
                margin: 2px 0 6px;
                font-size: 25px;
                line-height: 35px;
            }

            @container (min-width: 600px) {
                margin: 2px 0 13px;
            }
        }

        span {
            border: 2px solid $color-cta;
            border-radius: 100%;
            display: inline-block;
            width: 28px;
            height: 28px;
            line-height: 26px;
            font-size: 20px;
            text-align: center;
            font-weight: bold;

            @container (min-width: 300px) {
                width: 38px;
                height: 38px;
                line-height: 33px;
                font-size: 24px;
            }
        }

        &--dark {
            a {
                color: $color-dark;
            }

            h2 {
                color: $color-dark;
            }
        }

        &--brand {
            a {
                color: $color-cta;
            }

            h2 {
                color: $color-cta;
            }
        }

        &--white {
            a {
                color: $color-white;
            }

            h2 {
                color: $color-white;
            }
        }
    }
}
</style>
